import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Mail, Lock, ArrowRight, AlertCircle } from "lucide-react";
import { adminAPI } from "../services/api";

const AdminLogin = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await adminAPI.login(formData.email, formData.password);

      // Store token
      localStorage.setItem("admin_token", response.access_token);

      // Redirect to admin dashboard
      navigate("/admin/dashboard");
    } catch (err) {
      setError(err.response?.data?.detail || "Invalid email or password");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12 bg-[var(--background)]">
      <div className="max-w-md w-full">
        {/* Header */}
        <div className="text-center mb-12">
          <Link to="/" className="inline-block mb-8">
            <h1 className="font-headline text-2xl font-bold text-[var(--primary)] uppercase tracking-headline">
              Kravion
            </h1>
          </Link>
          <h2 className="font-headline text-3xl font-bold uppercase mb-2">
            Admin Login
          </h2>
          <p className="font-body text-[var(--on-surface-variant)]">
            Access your dashboard
          </p>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-[var(--error-container)] border-l-4 border-[var(--error)] flex items-start gap-3">
            <AlertCircle
              size={20}
              className="text-[var(--error)] flex-shrink-0 mt-0.5"
            />
            <p className="font-body text-sm text-[var(--error)]">{error}</p>
          </div>
        )}

        {/* Login Form */}
        <form
          onSubmit={handleSubmit}
          className="bg-[var(--surface-container)] p-8 space-y-6"
        >
          {/* Email */}
          <div>
            <label
              htmlFor="email"
              className="block font-body text-xs tracking-code uppercase text-[var(--on-surface-variant)] mb-3"
            >
              Email Address
            </label>
            <div className="relative">
              <Mail
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--on-surface-variant)]"
              />
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                className="w-full bg-[var(--surface-container-high)] border border-[var(--outline-variant)]/20 focus:border-[var(--primary)] outline-none pl-12 pr-4 py-3 font-body text-[var(--on-surface)] transition-colors duration-300"
                placeholder="admin@example.com"
              />
            </div>
          </div>

          {/* Password */}
          <div>
            <label
              htmlFor="password"
              className="block font-body text-xs tracking-code uppercase text-[var(--on-surface-variant)] mb-3"
            >
              Password
            </label>
            <div className="relative">
              <Lock
                size={20}
                className="absolute left-4 top-1/2 -translate-y-1/2 text-[var(--on-surface-variant)]"
              />
              <input
                type="password"
                id="password"
                name="password"
                required
                value={formData.password}
                onChange={handleChange}
                className="w-full bg-[var(--surface-container-high)] border border-[var(--outline-variant)]/20 focus:border-[var(--primary)] outline-none pl-12 pr-4 py-3 font-body text-[var(--on-surface)] transition-colors duration-300"
                placeholder="••••••••"
              />
            </div>
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading}
            className="magnetic w-full bg-gradient-to-br from-[var(--primary)] to-[var(--primary-container)] text-[var(--on-primary-container)] px-8 py-4 font-headline font-bold text-sm uppercase tracking-code hover:brightness-110 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-300 flex items-center justify-center gap-2 group"
          >
            {loading ? "Logging in..." : "Login"}
            {!loading && (
              <ArrowRight
                size={18}
                className="group-hover:translate-x-1 transition-transform"
              />
            )}
          </button>
        </form>

        {/* Register Link */}
        <p className="text-center mt-6 font-body text-sm text-[var(--on-surface-variant)]">
          Don't have an account?{" "}
          <Link
            to="/admin/register"
            className="text-[var(--primary)] hover:brightness-110 font-bold"
          >
            Register here
          </Link>
        </p>

        {/* Default Credentials (for development) */}
        <div className="mt-8 p-4 bg-[var(--surface-container-low)] border border-[var(--outline-variant)]/20">
          <p className="font-body text-xs text-[var(--on-surface-variant)] mb-2">
            <strong>Default Admin:</strong>
          </p>
          <p className="font-mono text-xs text-[var(--primary)]">
            Email: admin@kineticether.com
            <br />
            Password: admin123
          </p>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
